/**
 * 模型选择器组件
 *
 * 提供下拉菜单选择AI模型功能，支持：
 * - 显示当前选中的模型
 * - 从 modelStore 获取可用模型列表
 * - 下拉菜单切换模型
 * - 点击外部区域关闭菜单
 * - 键盘导航支持
 * - 禁用状态处理
 * - 加载和错误状态处理
 * - 模型详情提示
 * - 淡黄色主题配色
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getModelDisplayName } from '../../types/chat';
import { useModelStore } from '../../store/modelStore';

// 复用 modelStore 的 ModelInfo 类型
import type { ModelInfo } from '../../store/modelStore';

/**
 * ModelSelector组件属性接口
 */
interface ModelSelectorProps {
  /** 当前选中的模型 */
  currentModel: string;
  /** 模型切换回调函数 */
  onModelChange: (model: string) => void;
  /** 是否禁用选择器 */
  disabled?: boolean;
  /** JWT认证令牌 */
  token?: string;
}

/**
 * 向下箭头图标
 */
const ChevronDownIcon: React.FC<{ className?: string; isOpen: boolean }> = ({ className = 'w-4 h-4', isOpen }) => (
  <svg
    className={`transition-transform ${isOpen ? 'rotate-180' : ''} ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

/**
 * 加载图标
 */
const SpinnerIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

/**
 * ModelSelector组件
 *
 * 使用React.memo优化性能，避免不必要的重新渲染
 */
export const ModelSelector: React.FC<ModelSelectorProps> = React.memo(({
  currentModel,
  onModelChange,
  disabled = false,
  token
}) => {
  // ===== 状态管理 =====
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);

  // 从 modelStore 获取模型数据
  const availableModels = useModelStore((state) => state.availableModelIds);
  const modelsInfo = useModelStore((state) => state.modelsInfo);
  const isLoading = useModelStore((state) => state.isLoading);
  const error = useModelStore((state) => state.error);
  const fetchModels = useModelStore((state) => state.fetchModels);
  const clearError = useModelStore((state) => state.clearError);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  /**
   * 获取可用模型列表（使用 modelStore 缓存）
   */
  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  /**
   * 重试获取模型列表
   */
  const handleRetry = useCallback(() => {
    clearError();
    fetchModels();
  }, [fetchModels, clearError]);

  // ===== 事件处理函数 =====

  /**
   * 切换下拉菜单显示状态
   */
  const toggleMenu = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => {
      const newState = !prev;
      if (newState) {
        // 打开菜单时，聚焦当前选中的模型
        const currentIndex = availableModels.indexOf(currentModel);
        setFocusedIndex(currentIndex >= 0 ? currentIndex : 0);
      } else {
        setFocusedIndex(null);
      }
      return newState;
    });
  }, [disabled, currentModel, availableModels]);

  /**
   * 关闭下拉菜单
   */
  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setFocusedIndex(null);
  }, []);

  /**
   * 选择模型
   */
  const selectModel = useCallback((model: string) => {
    if (disabled) return;
    onModelChange(model);
    closeMenu();
  }, [disabled, onModelChange, closeMenu]);

  /**
   * 处理按钮键盘事件
   */
  const handleButtonKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        toggleMenu();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        }
        break;
      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          closeMenu();
        }
        break;
    }
  }, [disabled, isOpen, toggleMenu, closeMenu]);

  /**
   * 处理列表键盘事件
   */
  const handleListKeyDown = useCallback((e: React.KeyboardEvent<HTMLUListElement>) => {
    if (!isOpen || disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => {
          if (prev === null) return 0;
          return (prev + 1) % availableModels.length;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => {
          if (prev === null) return availableModels.length - 1;
          return (prev - 1 + availableModels.length) % availableModels.length;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex !== null && focusedIndex >= 0 && focusedIndex < availableModels.length) {
          selectModel(availableModels[focusedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        closeMenu();
        buttonRef.current?.focus();
        break;
    }
  }, [isOpen, disabled, availableModels, focusedIndex, selectModel, closeMenu]);

  /**
   * 点击外部区域关闭菜单
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeMenu]);

  /**
   * 确保聚焦的选项可见
   */
  useEffect(() => {
    if (isOpen && focusedIndex !== null && listRef.current) {
      const focusedElement = listRef.current.children[focusedIndex] as HTMLElement;
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [isOpen, focusedIndex]);

  // ===== 渲染函数 =====

  return (
    <div
      ref={containerRef}
      className="relative inline-block text-left"
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-label="模型选择器"
    >
      {/* 下拉按钮 */}
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleMenu}
        onKeyDown={handleButtonKeyDown}
        disabled={disabled || isLoading}
        className={`
          inline-flex items-center justify-center px-3 py-1.5
          text-xs text-gray-700 hover:text-gray-900
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
          focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
          ${disabled || isLoading ? '' : 'cursor-pointer'}
        `}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={`当前模型: ${getModelDisplayName(currentModel, modelsInfo[currentModel])}`}
      >
        {isLoading ? (
          <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />
        ) : error ? (
          <span className="text-red-600 text-xs">模型错误</span>
        ) : (
          <>
            <span className="truncate max-w-[120px]">{getModelDisplayName(currentModel, modelsInfo[currentModel])}</span>
            <ChevronDownIcon className="ml-1 w-3.5 h-3.5" isOpen={isOpen} />
          </>
        )}
      </button>

      {/* 错误提示 */}
      {error && isOpen && (
          <div className="absolute right-0 bottom-full mb-2 w-64 p-2.5 bg-red-50 border border-red-200 rounded-lg shadow-sm z-50">
          <p className="text-xs text-red-700 mb-2">{error}</p>
          <button
            onClick={handleRetry}
            className="w-full px-3 py-1 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            重试
          </button>
        </div>
      )}

      {/* 下拉菜单 */}
      {isOpen && !error && (
        <ul
          ref={listRef}
          role="listbox"
          className={`
            absolute right-0 bottom-full mb-2 w-56
            bg-white border border-gray-200 rounded shadow-sm z-50
            max-h-56 overflow-y-auto
            py-1
          `}
          onKeyDown={handleListKeyDown}
          tabIndex={-1}
        >
          {availableModels.length === 0 ? (
            <li className="px-3 py-2 text-xs text-gray-600 text-center">
              {isLoading ? '加载中...' : '无可用模型'}
            </li>
          ) : (
            availableModels.map((model, index) => {
              const isSelected = model === currentModel;
              const isFocused = index === focusedIndex;
              const modelInfo = modelsInfo[model];

              return (
                <li
                  key={model}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => selectModel(model)}
                  onMouseEnter={() => setHoveredModel(model)}
                  onMouseLeave={() => setHoveredModel(null)}
                  className={`
                    px-3 py-1.5 text-xs cursor-pointer relative
                    transition-colors
                    ${isSelected
                      ? 'bg-gray-50 text-gray-900 font-medium'
                      : 'text-gray-800 hover:bg-white'
                    }
                    ${isFocused
                      ? 'bg-white outline-none'
                      : ''
                    }
                    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                  tabIndex={-1}
                >
                  <div className="flex flex-col">
                    <span className="truncate">{getModelDisplayName(model, modelInfo)}</span>
                    <div className="flex items-center gap-1 mt-0.5">
                      {modelInfo?.provider && (
                        <span className="text-[10px] text-gray-600">
                          {modelInfo.provider}
                        </span>
                      )}
                      {modelInfo?.supports_thinking && (
                        <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">
                          思考
                        </span>
                      )}
                    </div>
                  </div>

                  {/* 模型详情提示 - 改为左侧显示，避免超出屏幕 */}
                  {hoveredModel === model && modelInfo && (
                    <div className="absolute right-full mr-2 top-0 w-60 p-2.5 bg-gray-900 text-white text-[10px] rounded shadow-sm z-[60]">
                      <h4 className="font-semibold mb-1 text-xs">{modelInfo.name}</h4>
                      {modelInfo.description && (
                        <p className="mb-1.5 opacity-90">{modelInfo.description}</p>
                      )}
                      <div className="space-y-0.5 opacity-75">
                        {modelInfo.provider && (
                          <p><strong>提供商:</strong> {modelInfo.provider}</p>
                        )}
                        {modelInfo.max_tokens && (
                          <p><strong>最大Token:</strong> {modelInfo.max_tokens.toLocaleString()}</p>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
});

ModelSelector.displayName = 'ModelSelector';

export default ModelSelector;